// T-181 — widget « indicateur » : les tuiles de la liste d'indicateurs du TENANT (GET /api/metier/tableau-de-bord).
// Le serveur calcule (jamais le navigateur) ; un clic sur une tuile demande l'ouverture de la grille unique filtrée via l'événement `oh:ouvrir-tuile`
// (écouté par OmniHub). Couleurs : tokens --oh-* uniquement (clair ET foncé).
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { enteteTenant } from "../../utils/enteteTenant";
import { tenantResolu } from "../../utils/tenantResolu";

const API = process.env.REACT_APP_BACKEND_URL;
export const EVENEMENT_OUVRIR_TUILE = "oh:ouvrir-tuile";
const PERIODES = [["mois", "Ce mois"], ["trimestre", "Ce trimestre"], ["annee", "Cette année"]];

export const formatValeur = (t) => {
  if (t.valeur == null) return "—";
  if (t.unite === "EUR") return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(t.valeur);
  return new Intl.NumberFormat("fr-FR").format(t.valeur);
};

/** Aplatit les indicateurs : un P1 (« par phase ») donne une tuile par onglet, les autres une tuile chacun. */
export function tuilesDe(indicateurs) {
  const out = [];
  for (const i of indicateurs || []) {
    if (Array.isArray(i.tuiles)) i.tuiles.forEach((t) => out.push({ ...t, parent: i.label }));
    else out.push(i);
  }
  return out;
}

export function ouvrirTuile(ouvre) {
  if (!ouvre) return false;
  window.dispatchEvent(new CustomEvent(EVENEMENT_OUVRIR_TUILE, { detail: ouvre }));
  return true;
}

export function Tuile({ t }) {
  const cliquable = !!t.ouvre && !t.erreur;
  const corps = (
    <>
      <span className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>{t.label}</span>
      {t.erreur
        ? <span className="text-xs mt-1" style={{ color: "var(--oh-error-text)" }}>{t.erreur}</span>
        : <span className="text-2xl font-medium mt-1" style={{ color: "var(--oh-text)" }} data-testid={`tuile-valeur-${t.id}`}>{formatValeur(t)}</span>}
      {t.source && <span className="text-xs mt-1" style={{ color: "var(--oh-text-muted)" }}>{t.source}</span>}
      {t.sans_montant > 0 && <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{t.sans_montant} sans montant enregistré</span>}
      {(t.avertissement || t.note) && <span className="text-xs mt-1" style={{ color: "var(--oh-text-muted)" }}>{t.avertissement || t.note}</span>}
    </>
  );
  const style = { background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: 12, padding: 16, textAlign: "left" };
  return cliquable
    ? <button type="button" onClick={() => ouvrirTuile(t.ouvre)} className="flex flex-col" style={{ ...style, cursor: "pointer" }} data-testid={`tuile-${t.id}`}>{corps}</button>
    : <div className="flex flex-col" style={style} data-testid={`tuile-${t.id}`}>{corps}</div>;
}

export default function IndicateursWidget({ title }) {
  const [periode, setPeriode] = useState("mois");
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    let vivant = true;
    setData(null); setErreur(null);
    const token = localStorage.getItem("session_token");
    fetch(`${API}/api/metier/tableau-de-bord?periode=${periode}`, {
      credentials: "include",
      headers: { ...enteteTenant(tenantResolu()), ...(token && { Authorization: `Bearer ${token}` }) },
    })
      .then(async (r) => {
        if (r.ok) return r.json();
        throw new Error(r.status === 403 ? "Le tableau de bord n'est pas ouvert pour cet espace ou pour votre rôle." : "Chargement impossible");
      })
      .then((d) => vivant && setData(d))
      .catch((e) => vivant && setErreur(e.message));
    return () => { vivant = false; };
  }, [periode]);

  const tuiles = tuilesDe(data?.indicateurs);
  return (
    <div className="rounded-xl p-5 h-full" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }} data-testid="widget-indicateurs">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>{title || "Tableau de bord"}</p>
        <select value={periode} onChange={(e) => setPeriode(e.target.value)} aria-label="Période" data-testid="indicateurs-periode"
          className="rounded px-2 py-1 text-xs" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)", colorScheme: "light dark" }}>
          {PERIODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {erreur && <p className="text-sm" style={{ color: "var(--oh-error-text)" }} role="alert">{erreur}</p>}
      {!erreur && !data && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--oh-text-muted)" }} /></div>}
      {data && tuiles.length === 0 && <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Aucun indicateur n'est configuré pour cet espace.</p>}
      {data && tuiles.length > 0 && (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
          {tuiles.map((t) => <Tuile key={`${t.parent || ""}${t.id}`} t={t} />)}
        </div>
      )}
    </div>
  );
}
